import { Check, X } from "lucide-react";
import { motion } from "motion/react";
import { cn } from "./cn";

/**
 * The article's 工程 (process). Wide screens: one line like a scroll, with the
 * finished part inked in indigo and the current step in vermilion. Narrow
 * screens: the same steps as labels that wrap.
 */
export function Steps({ steps, current, failed }: { steps: string[]; current: number; failed?: boolean }) {
  const progress = steps.length > 1 ? Math.min(current, steps.length - 1) / (steps.length - 1) : 0;
  return (
    <div>
      <ol className="relative hidden grid-flow-col lg:grid" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }} aria-label="進み具合">
        {/* The line between the first and last nodes, then the inked part. */}
        <span aria-hidden className="absolute top-[15px] h-[3px] rounded-full bg-line" style={{ left: `${50 / steps.length}%`, right: `${50 / steps.length}%` }} />
        <motion.span
          aria-hidden
          className="absolute top-[15px] h-[3px] origin-left rounded-full bg-brand"
          style={{ left: `${50 / steps.length}%`, width: `${(100 - 100 / steps.length) * progress}%` }}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.9, ease: [0.6, 0, 0.2, 1] }}
        />
        {steps.map((s, i) => {
          const done = i < current;
          const active = i === current;
          return (
            <li key={s} className="relative flex flex-col items-center gap-1.5 text-center" aria-current={active ? "step" : undefined}>
              <span
                className={cn(
                  "relative z-10 flex size-[33px] items-center justify-center rounded-full border-2 font-display text-[13px] font-bold transition-colors",
                  done && "border-brand bg-brand text-white",
                  active && !failed && "border-accent bg-surface text-accent",
                  active && failed && "border-bad bg-bad text-white",
                  !done && !active && "border-line-strong bg-surface text-muted",
                )}
              >
                {active && !failed && <span aria-hidden className="absolute inset-0 animate-ping rounded-full border-2 border-accent/40" />}
                {done ? <Check className="size-4" /> : active && failed ? <X className="size-4" /> : i + 1}
              </span>
              <span className={cn("px-1 text-xs leading-snug", active ? "font-bold text-ink" : done ? "text-ink-2" : "text-muted")}>{s}</span>
            </li>
          );
        })}
      </ol>

      <ol className="flex flex-wrap gap-x-1 gap-y-2 lg:hidden" aria-label="進み具合">
        {steps.map((s, i) => {
          const done = i < current;
          const active = i === current;
          return (
            <li key={s} className="flex items-center gap-1" aria-current={active ? "step" : undefined}>
              <span
                className={cn(
                  "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold whitespace-nowrap",
                  done && "border-brand bg-brand text-white",
                  active && !failed && "border-accent bg-surface text-accent",
                  active && failed && "border-bad bg-bad-light text-bad",
                  !done && !active && "border-line bg-surface text-muted",
                )}
              >
                <span className={cn("flex size-4 items-center justify-center rounded-full text-[10px]", done ? "bg-white/25" : "bg-current/10")}>{done ? <Check className="size-3" /> : i + 1}</span>
                {s}
              </span>
              {i < steps.length - 1 && <span className="h-px w-2 bg-line-strong" aria-hidden />}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
